import { useDispatch } from 'react-redux'
import { useLoginGetUserMutation } from '../react-query/authentication/mutation/use-login-logged-user-mutation'
import { useLoginRefreshMutation } from '../react-query/authentication/mutation/use-login-refresh-mutation'
import {
  useAuthStore,
  useIsLoggedIn,
  useTokenAvailable
} from '../zustand/authentication/use-auth-store'
import { useMeQuery } from '../react-query/authentication/query/use-me-query'
import { useEffect } from 'react'
import { isEmpty } from 'lodash'
import { setUser } from '../containers/authentication/actions'

/**
 * AuthHook
 * Responsável pela:
 * - Validação de login
 * - Refresh de token
 * - Carregamento do usuário logado
 * - Tratamento de erros de autenticação
 */

const useAuthHook = () => {
  const dispatch = useDispatch()

  const { mutate: handleGetUser } = useLoginGetUserMutation()

  const {
    mutate: handleRefreshToken,
    isPending: isLoadingRefreshToken,
    error: isErrorRefreshtoken
  } = useLoginRefreshMutation()

  const { initializedApp, expiredToken, setShowLoginPopup, logout } =
    useAuthStore()

  const tokenAvailable = useTokenAvailable()
  const isLoggedIn = useIsLoggedIn()

  const {
    data: user,
    isLoading: isLoadingUser,
    refetch: getUser,
    error: isErrorUser
  } = useMeQuery({
    enabled: tokenAvailable
  })

  const isLoadingLogin =
    isLoadingRefreshToken || !initializedApp || isLoadingUser

  // Logout on user error
  useEffect(() => {
    if (isErrorUser) {
      logout()
    }
  }, [isErrorUser, logout])

  // Show login popup on token refresh error
  useEffect(() => {
    if (isErrorRefreshtoken) {
      setShowLoginPopup(true)
    }
  }, [isErrorRefreshtoken, setShowLoginPopup])

  // Persist user to redux
  useEffect(() => {
    if (tokenAvailable && !isEmpty(user)) {
      dispatch(setUser(user))
    }
  }, [user, tokenAvailable, dispatch])

  // Fetch user on initial load
  useEffect(() => {
    if (tokenAvailable && isEmpty(user) && !isLoadingLogin) {
      getUser().then(({ data }) => {
        handleGetUser({ data })
      })
    }
  }, [tokenAvailable, user, isLoadingLogin, getUser, handleGetUser])

  // Refresh token on expiration
  useEffect(() => {
    if (expiredToken) {
      handleRefreshToken()
    }
  }, [expiredToken, handleRefreshToken])

  return {
    user,
    isLoggedIn,
    isLoadingLogin,
    tokenAvailable
  }
}

export default useAuthHook
